/**
 * xp1_HybridD - "Depth Cinema"
 * 
 * Hybrid: Cinematic Hero (xp1-a) + Immersive Depth (xp3-a) + Neon Cinema (xp3-hybrid-d)
 * 
 * Features:
 * - Dramatic cinematic hero with 3D perspective
 * - Scroll-triggered stacking cards with depth layers
 * - Neon accents and glowing elements
 * - High visual impact with smooth transitions
 */

import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@services/auth';
import './xp1_HybridD.css';

// Images
const overviewImg = '/showcase/overview_page.png';
const accountsImg = '/showcase/accounts_page.png';
const analyticsImg = '/showcase/ana_page.png';
const categoryImg = '/showcase/category_page.png';
const settingsImg = '/showcase/settings_page.jpg';

const DEPTH_CARDS = [
  {
    id: 'visualize',
    num: '01',
    title: 'VISUALIZE',
    subtitle: 'Complete Overview',
    desc: 'See your entire financial reality in one stunning dashboard. Net worth, cash flow, and recent activity at a glance.',
    img: overviewImg,
    color: '#7aa2f7',
  },
  {
    id: 'analyze',
    num: '02',
    title: 'ANALYZE',
    subtitle: 'Deep Insights',
    desc: 'Pattern recognition and cash flow scoring. Understand not just where money goes, but why.',
    img: analyticsImg,
    color: '#bb9af7',
  },
  {
    id: 'organize',
    num: '03',
    title: 'ORGANIZE',
    subtitle: 'Smart Categories',
    desc: '64+ preset categories with custom icons. Structure your financial life your way.',
    img: categoryImg,
    color: '#9ece6a',
  },
  {
    id: 'optimize',
    num: '04',
    title: 'OPTIMIZE',
    subtitle: 'Full Control',
    desc: 'Nine dark themes, privacy modes, and import/export. Make it truly yours.',
    img: settingsImg,
    color: '#e0af68',
  },
];

export default function xp1_HybridD() {
  const { signInWithGoogle, isLoading } = useAuth();
  const [scrollY, setScrollY] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImg((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleGoogleSignIn = async () => {
    const { error } = await signInWithGoogle();
    if (error) {
      console.error('[LOGIN] Failed to sign in:', error.message);
    }
  };

  const heroImages = [accountsImg, overviewImg, analyticsImg];
  const heroOpacity = Math.max(0, 1 - scrollY / 600);
  const heroScale = Math.max(0.8, 1 - scrollY / 3000);

  return (
    <div className={`xp1hd ${mounted ? 'mounted' : ''}`} ref={containerRef}>
      {/* Background Glow */}
      <div className="xp1hd-bg-glow" />

      {/* Header */}
      <header className="xp1hd-header">
        <div className="xp1hd-logo">
          <span className="xp1hd-logo-symbol">$</span>
          <span className="xp1hd-logo-text">NINJACASH</span>
        </div>
        <nav className="xp1hd-nav">
          <Link to="/xp1" className="xp1hd-nav-link">Hub</Link>
          <button
            className="xp1hd-nav-cta"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Launch'}
          </button>
        </nav>
      </header>

      {/* Cinematic Hero */}
      <section 
        className="xp1hd-hero"
        style={{ 
          opacity: heroOpacity,
          transform: `scale(${heroScale})`,
        }}
      >
        <div className="xp1hd-hero-content">
          <div className="xp1hd-hero-tag">
            <span className="xp1hd-tag-pulse" />
            PERSONAL FINANCE SYSTEM
          </div>

          <h1 className="xp1hd-hero-title">
            <span className="xp1hd-title-line">FUTURE</span>
            <span className="xp1hd-title-line xp1hd-title-glow">FINANCE</span>
          </h1>

          <p className="xp1hd-hero-subtitle">
            Track. Sync. Conquer.
          </p>

          <p className="xp1hd-hero-desc">
            The offline-first finance tracker that respects your privacy.
            No ads. No tracking. No bank connections. Just you and your money.
          </p>

          <div className="xp1hd-hero-actions">
            <button
              className="xp1hd-btn-primary"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {isLoading ? 'Entering...' : 'Enter System'}
            </button>
          </div>

          <div className="xp1hd-hero-stats">
            <div className="xp1hd-stat">
              <span className="xp1hd-stat-value">100%</span>
              <span className="xp1hd-stat-label">Offline</span>
            </div>
            <div className="xp1hd-stat-divider" />
            <div className="xp1hd-stat">
              <span className="xp1hd-stat-value">9</span>
              <span className="xp1hd-stat-label">Themes</span>
            </div>
            <div className="xp1hd-stat-divider" />
            <div className="xp1hd-stat">
              <span className="xp1hd-stat-value">$0</span>
              <span className="xp1hd-stat-label">Free Tier</span>
            </div>
          </div>
        </div>

        {/* 3D Device */}
        <div className="xp1hd-hero-device">
          <div className="xp1hd-device-wrapper">
            <div className="xp1hd-device-frame">
              {heroImages.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt="App preview"
                  className={`xp1hd-device-img ${i === activeImg ? 'active' : ''}`}
                />
              ))}
            </div>
            <div className="xp1hd-device-glow" />
            <div className="xp1hd-device-reflection" />
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="xp1hd-scroll-hint">
          <span>Scroll to explore</span>
          <div className="xp1hd-scroll-line" />
        </div>
      </section>

      {/* Stream Section with Central Line */}
      <section className="xp1hd-stream">
        <div className="xp1hd-stream-line" />

        {/* Depth Cards */}
        <div className="xp1hd-cards-container">
          {DEPTH_CARDS.map((card, index) => (
            <div
              key={card.id}
              className="xp1hd-card-wrapper"
              style={{ 
                '--card-color': card.color,
                '--card-index': index,
              } as React.CSSProperties}
            >
              <div className="xp1hd-card">
                <div className="xp1hd-card-content">
                  <div className="xp1hd-card-num">{card.num}</div>
                  <h2 className="xp1hd-card-title">{card.title}</h2>
                  <h3 className="xp1hd-card-subtitle">{card.subtitle}</h3>
                  <p className="xp1hd-card-desc">{card.desc}</p>
                </div>
                <div className="xp1hd-card-visual">
                  <div className="xp1hd-card-img-frame">
                    <img src={card.img} alt={card.title} className="xp1hd-card-img" />
                  </div>
                </div>
                <div className="xp1hd-card-glow" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="xp1hd-cta">
        <div className="xp1hd-cta-content">
          <h2 className="xp1hd-cta-title">
            <span className="xp1hd-title-glow">READY?</span>
          </h2>
          <p className="xp1hd-cta-desc">
            Enter the future of personal finance.
            Free forever for single device.
          </p>
          <button
            className="xp1hd-btn-primary xp1hd-btn-large"
            onClick={handleGoogleSignIn}
            disabled={isLoading}
          >
            {isLoading ? 'Initializing...' : 'Initialize System'}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="xp1hd-footer">
        <div className="xp1hd-footer-brand">
          <span className="xp1hd-logo-symbol xp1hd-logo-small">$</span>
          <span>NINJACASH</span>
        </div>
        <div className="xp1hd-footer-links">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="https://github.com/TakashiKyoto/NinjaCash" target="_blank" rel="noopener noreferrer">
            GitHub
          </a>
        </div>
        <div className="xp1hd-footer-copy">
          &copy; {new Date().getFullYear()} NinjaCash
        </div>
      </footer>
    </div>
  );
}
